import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import ApproveIcon from '@mui/icons-material/CheckCircleOutline';
import RejectIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { formattedDate } from '@luxuryescapes/lib-refunds/build/lib/formatDate';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { SUPPLIER_NAMES } from '~/consts/bedbank';

import BedbankService from '~/services/BedbankService';

interface MappingIssuesResponse {
  result: Array<App.Bedbank.MappingIssue>;
  count: number;
}

const PAGE_SIZE = 15;

const ISSUE_TYPES: Array<App.Bedbank.IssueType | 'SUPPLIER_MAPPING_ALREADY_MAPPED'> = [
  'MAPPING_UPDATE',
  'MAPPING_DUPLICATION',
  'MAPPING_ALREADY_MAPPED',
  'MAPPING_REMOVAL',
  'SUPPLIER_MAPPING_ALREADY_MAPPED',
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`issue-tabpanel-${index}`}
      aria-labelledby={`issue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface DuplicationInteractiveColumnOptions {
  onApproveDuplication: (issueId: string, supplierId: string) => void;
  onIgnoreDuplication: (issueId: string) => void;
  duplicationApprovePendingKey: string | null;
  duplicationIgnorePendingIssueId: string | null;
}

export default function MappingIssuesPage() {
  const [mappingIssues, setMappingIssues] = useState<Array<App.Bedbank.MappingIssue>>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedIssueIds, setSelectedIssueIds] = useState<Array<string>>([]);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | null;
    count: number;
  }>({
    open: false,
    action: null,
    count: 0,
  });
  const [comment, setComment] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [duplicationApprovePendingKey, setDuplicationApprovePendingKey] = useState<string | null>(null);
  const [duplicationIgnorePendingIssueId, setDuplicationIgnorePendingIssueId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [status, setStatus] = useState<'unresolved' | 'resolved'>('unresolved');
  const [futureBookingsOnly, setFutureBookingsOnly] = useState<boolean>(true);
  const [supplierFilter, setSupplierFilter] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const fetchMappingIssues = useCallback(
    async (
      issueType: App.Bedbank.IssueType,
      issueStatus: Array<string> = [],
      page: number,
      pageSize: number,
      withFutureBookings?: boolean,
      supplierName?: string,
    ) => {
      setLoading(true);
      try {
        const response: MappingIssuesResponse = await BedbankService.getMappingIssues({
          issueType,
          issueStatus,
          page: page + 1,
          limit: pageSize,
          withFutureBookings,
          supplierName: supplierName || undefined,
        });
        setMappingIssues(response.result);
        setTotalCount(response.count || 0);
      } catch (error) {
        enqueueSnackbar('Failed to fetch mapping issues', { variant: 'error' });
        console.error('Error fetching mapping issues:', error);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const getStatusFilter = useCallback((): Array<string> => {
    return status === 'unresolved' ? ['UNACTIONED', 'IN_PROGRESS'] : ['RESOLVED'];
  }, [status]);

  useEffect(() => {
    fetchMappingIssues(
      ISSUE_TYPES[activeTab],
      getStatusFilter(),
      paginationModel.page,
      paginationModel.pageSize,
      futureBookingsOnly,
      supplierFilter,
    );
  }, [paginationModel, fetchMappingIssues, activeTab, getStatusFilter, futureBookingsOnly, supplierFilter]);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  }, []);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page when changing tabs
  }, []);

  const handleStatusToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.checked ? 'resolved' : 'unresolved');
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleFutureBookingsToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFutureBookingsOnly(event.target.checked);
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleSupplierFilterChange = useCallback((value: string) => {
    setSupplierFilter(value);
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const getIssueTypeLabel = useCallback((issueType: App.Bedbank.IssueType | string) => {
    switch (issueType) {
      case 'MAPPING_UPDATE':
        return 'Supplier ID mismatch';
      case 'MAPPING_DUPLICATION':
        return 'Supplier ID duplication';
      case 'MAPPING_ALREADY_MAPPED':
        return 'Supplier ID already mapped';
      case 'MAPPING_REMOVAL':
        return 'Supplier ID removed';
      case 'SUPPLIER_MAPPING_ALREADY_MAPPED':
        return 'Auto-map conflict';
      default:
        return issueType;
    }
  }, []);

  const getActionCopy = useCallback((issueType: App.Bedbank.IssueType | string) => {
    switch (issueType) {
      case 'MAPPING_UPDATE':
      case 'MAPPING_DUPLICATION':
      case 'MAPPING_ALREADY_MAPPED':
        return 'Update Supplier';
      case 'MAPPING_REMOVAL':
        return 'Delete Supplier';
      case 'SUPPLIER_MAPPING_ALREADY_MAPPED':
        return 'Acknowledge';
      default:
        return 'Update Supplier';
    }
  }, []);

  const getIssueStatusLabel = useCallback((issueStatus: App.Bedbank.IssueStatus) => {
    switch (issueStatus) {
      case 'UNACTIONED':
        return 'Unresolved';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'RESOLVED':
        return 'Resolved';
    }
  }, []);

  const handleSelectIssue = useCallback((issueId: string, checked: boolean) => {
    setSelectedIssueIds((prev) => (checked ? [...prev, issueId] : prev.filter((id) => id !== issueId)));
  }, []);

  const handleBatchApprove = useCallback(() => {
    setConfirmationDialog({
      open: true,
      action: 'approve',
      count: selectedIssueIds.length,
    });
  }, [selectedIssueIds]);

  const handleBatchReject = useCallback(() => {
    setConfirmationDialog({
      open: true,
      action: 'reject',
      count: selectedIssueIds.length,
    });
  }, [selectedIssueIds]);

  const handleConfirmAction = useCallback(async () => {
    setSubmitting(true);
    try {
      let resolvedIssuesCount = 0;
      for (const issueId of selectedIssueIds) {
        try {
          await BedbankService.resolveMappingIssues({
            issueId,
            resolution: confirmationDialog.action,
            comments: comment,
          });
          resolvedIssuesCount += 1;
          enqueueSnackbar(`Resolved issue ${issueId}`, { variant: 'success' });
        } catch (error) {
          enqueueSnackbar(`Failed to resolve issue ${issueId}`, { variant: 'error' });
        }
      }

      if (confirmationDialog.action === 'approve' && resolvedIssuesCount > 0) {
        enqueueSnackbar(
          'Supplier mapping updated. Background maintenance tasks have started to prepare the property for sale.',
          { variant: 'success' },
        );
      }

      fetchMappingIssues(
        ISSUE_TYPES[activeTab],
        getStatusFilter(),
        paginationModel.page,
        paginationModel.pageSize,
        futureBookingsOnly,
        supplierFilter,
      );
      setSelectedIssueIds([]);
      setComment('');
      setConfirmationDialog({
        open: false,
        action: null,
        count: 0,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    confirmationDialog.action,
    selectedIssueIds,
    comment,
    enqueueSnackbar,
    activeTab,
    paginationModel.page,
    paginationModel.pageSize,
    getStatusFilter,
    futureBookingsOnly,
    supplierFilter,
  ]);

  const handleCancelAction = useCallback(() => {
    setComment('');
    setConfirmationDialog({
      open: false,
      action: null,
      count: 0,
    });
  }, []);

  const handleApproveDuplicationSuggestion = useCallback(
    async (issueId: string, supplierId: string) => {
      const pendingKey = `${issueId}:${supplierId}`;
      setDuplicationApprovePendingKey(pendingKey);
      try {
        await BedbankService.resolveMappingIssues({
          issueId,
          resolution: 'approve',
          selectedPropertyId: supplierId,
        });
        enqueueSnackbar(`Resolved issue ${issueId}`, { variant: 'success' });
        enqueueSnackbar(
          'Supplier mapping updated. Background maintenance tasks have started to prepare the property for sale.',
          { variant: 'success' },
        );
        fetchMappingIssues(
          ISSUE_TYPES[activeTab],
          getStatusFilter(),
          paginationModel.page,
          paginationModel.pageSize,
          futureBookingsOnly,
          supplierFilter,
        );
      } catch (error) {
        enqueueSnackbar(`Failed to resolve issue ${issueId}`, { variant: 'error' });
        console.error('Error resolving duplication issue', error);
      } finally {
        setDuplicationApprovePendingKey(null);
      }
    },
    [
      activeTab,
      enqueueSnackbar,
      fetchMappingIssues,
      futureBookingsOnly,
      getStatusFilter,
      paginationModel.page,
      paginationModel.pageSize,
      supplierFilter,
    ],
  );

  const handleIgnoreDuplicationIssue = useCallback(
    async (issueId: string) => {
      setDuplicationIgnorePendingIssueId(issueId);
      try {
        await BedbankService.resolveMappingIssues({
          issueId,
          resolution: 'reject',
        });
        enqueueSnackbar(`Ignored issue ${issueId}`, { variant: 'success' });
        fetchMappingIssues(
          ISSUE_TYPES[activeTab],
          getStatusFilter(),
          paginationModel.page,
          paginationModel.pageSize,
          futureBookingsOnly,
          supplierFilter,
        );
      } catch (error) {
        enqueueSnackbar(`Failed to ignore issue ${issueId}`, { variant: 'error' });
        console.error('Error ignoring duplication issue', error);
      } finally {
        setDuplicationIgnorePendingIssueId(null);
      }
    },
    [
      activeTab,
      enqueueSnackbar,
      fetchMappingIssues,
      futureBookingsOnly,
      getStatusFilter,
      paginationModel.page,
      paginationModel.pageSize,
      supplierFilter,
    ],
  );

  const typesThatShowSuggestedSupplierIdColumn = useMemo(() => {
    return ['MAPPING_DUPLICATION', 'MAPPING_UPDATE'];
  }, []);
  const shouldShowSuggestedSupplierIdColumn = useCallback((issueType: App.Bedbank.IssueType | string) => {
    return typesThatShowSuggestedSupplierIdColumn.includes(issueType);
  }, []);

  const shouldShowSuggestedPropertyColumn = useCallback((issueType: App.Bedbank.IssueType | string) => {
    return issueType === 'MAPPING_ALREADY_MAPPED';
  }, []);

  const getColumns = useCallback(
    (
      currentIssueType: App.Bedbank.IssueType | string,
      duplicationInteractive?: DuplicationInteractiveColumnOptions,
    ): Array<GridColDef<App.Bedbank.MappingIssue>> => {
      const baseColumns: Array<GridColDef<App.Bedbank.MappingIssue>> = [
        {
          field: 'issueStatus',
          headerName: 'Status',
          flex: 1,
          minWidth: 80,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {getIssueStatusLabel(params.row.issueStatus)}
            </Typography>
          ),
        },
        {
          field: 'issueData.propertyName',
          headerName: 'Property Name',
          flex: 1,
          minWidth: 350,
          renderCell: (params) => (
            <Link
              style={{ display: 'flex', alignItems: 'center', height: '100%' }}
              to={`/bedbank/properties/${params.row.issueData.propertyId}`}
            >
              {params.row.issueData.propertyName}
            </Link>
          ),
        },
        {
          field: 'issueData.masterSupplier',
          headerName: `Master Supplier`,
          flex: 1,
          minWidth: 200,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {params.row.issueData.masterSupplier}
            </Typography>
          ),
        },
        {
          field: 'issueData.masterSupplierPropertyId',
          headerName: `Master Supplier ID`,
          flex: 1,
          minWidth: 200,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {params.row.issueData.masterSupplierPropertyId}
            </Typography>
          ),
        },
        {
          field: 'issueData.supplierName',
          headerName: 'Supplier',
          flex: 1,
          minWidth: 150,
          renderCell: (params) => (
            <Typography
              variant="body2"
              sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
            >
              {params.row.issueData.supplierName}
            </Typography>
          ),
        },
        {
          field: 'issueData.currentSupplierId',
          headerName: 'Current Supplier ID',
          flex: 1,
          minWidth: 150,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {params.row.issueData.currentSupplierId}
            </Typography>
          ),
        },
      ];

      const suggestedSupplierIdsColumn: GridColDef<App.Bedbank.MappingIssue> = {
        field: 'issueData.suggestedSupplierProperties',
        headerName: 'Suggested Supplier IDs',
        flex: 1,
        minWidth: 280,
        renderCell: (params) => {
          const issueData = params.row.issueData as App.Bedbank.DuplicationMappingIssueData &
            App.Bedbank.UpdatedMappingIssueData;
          const suggestions = issueData.suggestedSupplierProperties
            ? issueData.suggestedSupplierProperties
            : issueData.suggestedSupplierProperty
            ? [issueData.suggestedSupplierProperty]
            : [];
          if (!suggestions || suggestions.length === 0) {
            return (
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                No suggestions
              </Typography>
            );
          }

          const isDuplicationWithActions = currentIssueType === 'MAPPING_DUPLICATION' && duplicationInteractive;
          const isBusy =
            !!duplicationInteractive &&
            (duplicationInteractive.duplicationApprovePendingKey !== null ||
              duplicationInteractive.duplicationIgnorePendingIssueId !== null);

          if (isDuplicationWithActions) {
            return (
              <Stack spacing={1} sx={{ py: 0.5 }}>
                {suggestions.map((suggestion, index) => {
                  const pendingKey = `${params.row.id}:${suggestion.supplierId}`;
                  const isThisPending = duplicationInteractive.duplicationApprovePendingKey === pendingKey;
                  const label = (
                    <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                      {suggestion.propertyId ? (
                        <Link to={`/bedbank/properties/${suggestion.propertyId}`}>{suggestion.supplierId}</Link>
                      ) : (
                        <Typography variant="body2" component="span">
                          {suggestion.supplierId}
                        </Typography>
                      )}
                    </Box>
                  );
                  return (
                    <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                      {label}
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={isBusy}
                        onClick={() =>
                          duplicationInteractive.onApproveDuplication(params.row.id, suggestion.supplierId)
                        }
                        startIcon={isThisPending ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon />}
                      >
                        Approve
                      </Button>
                    </Stack>
                  );
                })}
              </Stack>
            );
          }

          return (
            <Box>
              {suggestions.map((suggestion, index) =>
                suggestion.propertyId ? (
                  <Link
                    style={{ display: 'flex', alignItems: 'center', height: '100%' }}
                    key={index}
                    to={`/bedbank/properties/${suggestion.propertyId}`}
                  >
                    {suggestion.supplierId}
                  </Link>
                ) : (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
                  >
                    {suggestion.supplierId}
                  </Typography>
                ),
              )}
            </Box>
          );
        },
      };

      const duplicationActionsColumn: GridColDef<App.Bedbank.MappingIssue> = {
        field: 'duplicationActions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (!duplicationInteractive) {
            return null;
          }
          const isBusy =
            duplicationInteractive.duplicationApprovePendingKey !== null ||
            duplicationInteractive.duplicationIgnorePendingIssueId !== null;
          const isIgnoring = duplicationInteractive.duplicationIgnorePendingIssueId === params.row.id;
          return (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={isBusy}
              onClick={() => duplicationInteractive.onIgnoreDuplication(params.row.id)}
              startIcon={isIgnoring ? <CircularProgress size={14} color="inherit" /> : <RejectIcon />}
            >
              Ignore
            </Button>
          );
        },
      };

      const suggestedPropertyColumn: GridColDef<App.Bedbank.MappingIssue> = {
        field: 'issueData.suggestedPropertyId',
        headerName: 'Suggested Property',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const issueData = params.row.issueData as App.Bedbank.AlreadyMappedMappingIssueData;
          return (
            <Link
              style={{ display: 'flex', alignItems: 'center', height: '100%' }}
              to={`/bedbank/properties/${issueData.suggestedPropertyId}`}
            >
              {issueData.suggestedPropertyName}
            </Link>
          );
        },
      };

      if (shouldShowSuggestedSupplierIdColumn(currentIssueType)) {
        if (currentIssueType === 'MAPPING_DUPLICATION' && duplicationInteractive) {
          return [...baseColumns, suggestedSupplierIdsColumn, duplicationActionsColumn];
        }
        return [...baseColumns, suggestedSupplierIdsColumn];
      }

      if (shouldShowSuggestedPropertyColumn(currentIssueType)) {
        return [...baseColumns, suggestedPropertyColumn];
      }

      return baseColumns;
    },
    [getIssueStatusLabel, shouldShowSuggestedSupplierIdColumn, shouldShowSuggestedPropertyColumn],
  );

  const isDuplicationTab = ISSUE_TYPES[activeTab] === 'MAPPING_DUPLICATION';

  const columnsUnresolved: Array<GridColDef<App.Bedbank.MappingIssue>> = useMemo(() => {
    const currentIssueType = ISSUE_TYPES[activeTab];
    const baseColumns = getColumns(
      currentIssueType,
      isDuplicationTab
        ? {
            onApproveDuplication: handleApproveDuplicationSuggestion,
            onIgnoreDuplication: handleIgnoreDuplicationIssue,
            duplicationApprovePendingKey,
            duplicationIgnorePendingIssueId,
          }
        : undefined,
    );

    const selectColumn: GridColDef<App.Bedbank.MappingIssue> = {
      field: 'select',
      headerName: 'Select',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSelected = selectedIssueIds.includes(params.row.id);

        const checkbox = (
          <Checkbox checked={isSelected} onChange={(event) => handleSelectIssue(params.row.id, event.target.checked)} />
        );

        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            {checkbox}
          </Box>
        );
      },
    };

    return [
      ...(isDuplicationTab ? [] : [selectColumn]),
      ...baseColumns,
      {
        field: 'issueData.futureBookings',
        headerName: 'Future Bookings',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const bookings = params.row.issueData.futureBookings ?? [];
          return (
            <Box>
              {bookings.map((booking, index) => (
                <Link key={index} to={`/purchases/booking-number/${booking.bookingNumber}`} target="_blank">
                  <Typography key={index}>
                    {booking.bookingNumber} - {booking.checkinDate}
                  </Typography>
                </Link>
              ))}
            </Box>
          );
        },
      },
    ];
  }, [
    activeTab,
    getColumns,
    selectedIssueIds,
    handleSelectIssue,
    isDuplicationTab,
    handleApproveDuplicationSuggestion,
    handleIgnoreDuplicationIssue,
    duplicationApprovePendingKey,
    duplicationIgnorePendingIssueId,
  ]);

  const columnsResolved: Array<GridColDef<App.Bedbank.MappingIssue>> = useMemo(() => {
    const currentIssueType = ISSUE_TYPES[activeTab];
    const baseColumns = getColumns(currentIssueType);

    return [
      ...baseColumns,
      {
        field: 'resolutions',
        headerName: 'Resolution',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          return (
            <Box>
              {params.row.resolutions.map((resolution, index) => (
                <Tooltip
                  key={index}
                  title={
                    <Box>
                      <Typography variant="body2">
                        <strong>Actioned by:</strong> {resolution.actionedBy}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Comments:</strong> {resolution.comments || 'No comment'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date:</strong> {formattedDate(new Date(resolution.createdAt))}
                      </Typography>
                    </Box>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 2,
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(255, 255, 255, 0.95)',
                          '&::before': {
                            border: '1px solid',
                            borderColor: 'divider',
                          },
                        },
                      },
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: 'help',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    {resolution.resolution === 'approve' ? 'Approved' : 'Rejected'}
                  </Typography>
                </Tooltip>
              ))}
            </Box>
          );
        },
      },
    ];
  }, [activeTab, getColumns]);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="mapping issue types" sx={{ flexGrow: 1 }}>
          {ISSUE_TYPES.map((issueType, index) => (
            <Tab
              key={issueType}
              label={getIssueTypeLabel(issueType)}
              id={`issue-tab-${index}`}
              aria-controls={`issue-tabpanel-${index}`}
            />
          ))}
        </Tabs>
        <Stack direction="row" spacing={2} sx={{ ml: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="supplier-filter-label">Supplier</InputLabel>
            <Select
              labelId="supplier-filter-label"
              value={supplierFilter}
              label="Supplier"
              onChange={(e) => handleSupplierFilterChange(e.target.value)}
            >
              <MenuItem value="">All Suppliers</MenuItem>
              {SUPPLIER_NAMES.map((supplier) => (
                <MenuItem key={supplier} value={supplier} sx={{ textTransform: 'capitalize' }}>
                  {supplier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch color="primary" checked={futureBookingsOnly} onChange={handleFutureBookingsToggle} />}
            label="Future bookings only"
            labelPlacement="start"
          />
          <FormControlLabel
            control={<Switch color="primary" checked={status === 'resolved'} onChange={handleStatusToggle} />}
            label={status === 'resolved' ? 'Resolved' : 'Unresolved'}
            labelPlacement="start"
          />
        </Stack>
      </Box>

      {ISSUE_TYPES.map((issueType, index) => (
        <TabPanel key={issueType} value={activeTab} index={index}>
          <Box
            sx={{
              backgroundColor: blue[50],
              p: 1,
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {status === 'unresolved' ? 'Unresolved' : 'Resolved'} {getIssueTypeLabel(issueType).toLowerCase()} issues
            </Typography>
            {status === 'unresolved' && issueType !== 'MAPPING_DUPLICATION' && (
              <Stack direction="row" spacing={1}>
                {!futureBookingsOnly && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleBatchApprove}
                    disabled={selectedIssueIds.length === 0}
                    startIcon={<ApproveIcon />}
                  >
                    {getActionCopy(issueType)} ({selectedIssueIds.length})
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleBatchReject}
                  disabled={selectedIssueIds.length === 0}
                  startIcon={<RejectIcon />}
                >
                  Ignore ({selectedIssueIds.length})
                </Button>
              </Stack>
            )}
          </Box>
          <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
              columns={status === 'unresolved' ? columnsUnresolved : columnsResolved}
              rows={mappingIssues}
              rowCount={totalCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
              getRowId={(row) => row.id}
              slots={{
                pagination: GridPagination,
              }}
              pageSizeOptions={[15, 25, 50, 100]}
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-row': {
                  minHeight: '32px !important',
                },
              }}
            />
          </Box>
        </TabPanel>
      ))}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.open}
        onClose={submitting ? undefined : handleCancelAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm {confirmationDialog.action === 'approve' ? 'approval' : 'rejection'} of {confirmationDialog.count}{' '}
          {confirmationDialog.count === 1 ? 'issue' : 'issues'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to {confirmationDialog.action} {confirmationDialog.count} mapping{' '}
            {confirmationDialog.count === 1 ? 'issue' : 'issues'}. You may add a comment to explain your decision
            (optional).
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Comment (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter your comment here..."
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} variant="text" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmationDialog.action === 'approve' ? 'success' : 'error'}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting
              ? 'Processing...'
              : confirmationDialog.action === 'approve'
              ? 'Confirm Approval'
              : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
